import React, {forwardRef, useEffect} from "react";
import {ArtistDTO, PlaylistCreateDTO, Song} from "../../types";
import {ScrollModal} from "../../../../shared/scroll-modal";
import {ScrollModalApi} from "../../../../shared/scroll-modal/view";
import {SongCard} from "../song-card";
import {OptionButton} from "../../../../shared/option-button";
import useSongStore from "../../store/SongState";
import toast from "react-hot-toast";
import {handleCopy} from "../../../../../utils/utils";
import {Spotip17anFunctions} from "../../../../../utils/api";
import useUserStore from "../../../../../store/UserState";
import CreatePlaylistModal from "../../../../shared/create-playlist-modal/create-playlist-modal";
import {useNavigate} from "react-router-dom";
import SeparatorLine from "../../../../shared/separator-line/separator-line";
import {PaginatedResponse} from "../../../../../utils/apiTypes";
import {deleteSong} from "../../../../../utils/api";
import {SongEditModal} from "../../../../shared/song-edit-modal";
import ConfirmationModal from "../../../../shared/confirmation-modal/confirmation-modal";

type SongOptionsModalViewProps = {
    song: Song | null;
    scrollRef: React.RefObject<ScrollModalApi>;
    setSong: (song: Song) => void;
    onDeleteSong?: (songId: string | number) => void;
    extraOptions?: React.ReactNode;
}

const SongOptionsModalView =
    forwardRef<ScrollModalApi, SongOptionsModalViewProps>(({
                                                               song,
                                                               setSong,
                                                               onDeleteSong,
                                                               scrollRef,
                                                               extraOptions
                                                           }: SongOptionsModalViewProps, ref) => {

        const {setCurrentSong, addSongToQueue, addSongToPlayNext, removeSongFromStore} = useSongStore(state => state);
        const [isFavorite, setIsFavorite] = React.useState(song?.is_favorite);
        const {user, spotip17anUser, offlineMode} = useUserStore(state => state.state);
        const navigate = useNavigate();
        const [playlists, setPlaylists] = React.useState<ArtistDTO[]>([]);
        const [loadingPlaylists, setLoadingPlaylists] = React.useState(false);
        const [selectingPlaylist, setSelectingPlaylist] = React.useState(false);
        const [loadingFavorite, setLoadingFavorite] = React.useState(false);
        const [showEditModal, setShowEditModal] = React.useState(false);
        const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
        const [deletingSong, setDeletingSong] = React.useState(false);

        const isOwner = Boolean(
            spotip17anUser && song && (
                (song.user_id && song.user_id === spotip17anUser.id) ||
                (song.user?.id && song.user.id === spotip17anUser.id) ||
                (spotip17anUser.spotip17an_artist && (
                    (song.user as any)?.spotip17anUid === user?.uid ||
                    (song.user?.username && song.user.username.toLowerCase() === spotip17anUser.username.toLowerCase())
                ))
            )
        );

        useEffect(() => {
            setIsFavorite(song?.is_favorite);
        }, [song]);

        const handleSetSong = () => {
            setCurrentSong(song!);
            scrollRef.current!.close();
        }

        const handleDownload = async () => {
            const response = await fetch(song!.song_url);
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = url;
            a.setAttribute('download', `${song!.name} - ${song!.user.username}.mp3`);

            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

            // Release the object URL after download to free memory
            URL.revokeObjectURL(url);

            scrollRef.current!.close();
        }

        const handleAddQueue = () => {
            addSongToQueue(song!);
            scrollRef.current?.close();
        }

        const handlePlayNext = () => {
            addSongToPlayNext(song!);
            scrollRef.current?.close();
        }

        const handleAddToList = () => {
            if (loadingPlaylists) return;
            setLoadingPlaylists(true);
            setSelectingPlaylist(true);
            Spotip17anFunctions.use('myPlaylists').call({max: Infinity}).then((res: PaginatedResponse<ArtistDTO>) => {
                setPlaylists(res.data.filter(p => p.id !== 'favs'));
            }).finally(() => {
                setLoadingPlaylists(false);
            });
        }

        const handleCopyLink = () => {
            handleCopy('https://spotip17an.es/share?song=' + song!.id);
            scrollRef.current?.close();
        }

        const handleAddToFavorite = () => {
            setLoadingFavorite(true);
            Spotip17anFunctions.use('addToFavorites').call({songId: song!.id}).then(() => {
                setLoadingFavorite(false);
                setSong({...song!, is_favorite: true});
                setIsFavorite(true);
                toast.success('Canción añadida a favoritos');
                scrollRef.current?.close();
            });
        };

        const handleRemoveFavorite = () => {
            setLoadingFavorite(true);
            Spotip17anFunctions.use('removeFromFavorites').call({songId: song!.id}).then(() => {
                setLoadingFavorite(false);
                setSong({...song!, is_favorite: false});
                setIsFavorite(false);
                toast.success('Canción eliminada de favoritos');
                scrollRef.current?.close();
            });
        };

        const handleCloseCreatePlaylist = () => {
            navigate('#');
        }

        const handleSavePlaylist = (playlist: PlaylistCreateDTO) => {
            Spotip17anFunctions.use('createPlaylist').call(playlist).then((res: ArtistDTO) => {
                handleCloseCreatePlaylist();
                setPlaylists([...playlists, res]);
            }).catch(e => {
                console.error(e);
            })
        }

        return (
            <div>
                <ScrollModal ref={ref} onClose={() => {
                    setSelectingPlaylist(false);
                }}
                             header={song && <SongCard className="mb-2" song={{...song, is_favorite: isFavorite}}/>
                             }>
                    {!selectingPlaylist &&
                        <>
                            <OptionButton icon={<i className="fa-solid fa-play"></i>}
                                          text="Reproducir" onClick={handleSetSong}/>
                            {!isFavorite &&
                                <OptionButton hidden={!user?.uid || loadingFavorite || offlineMode}
                                              icon={<i className="fa-regular fa-heart"></i>}
                                              text="Añadir a favoritos" onClick={handleAddToFavorite}/>}
                            {isFavorite &&
                                <OptionButton hidden={!user?.uid || loadingFavorite || offlineMode}
                                              icon={<i className="fa-solid fa-heart-crack"></i>}
                                              text="Quitar de favoritos" onClick={handleRemoveFavorite}/>}
                            <OptionButton icon={<i className="fa-solid fa-list-ol"></i>}
                                          text="Reproducir a continuación" onClick={handlePlayNext}/>
                            <OptionButton icon={<i className="fa-solid fa-plus"></i>}
                                          text="Añadir a la cola" onClick={handleAddQueue}/>
                            <OptionButton hidden={!user?.uid || offlineMode}
                                          icon={<i className="fa-regular fa-square-plus"></i>}
                                          text="Añadir a lista" onClick={handleAddToList}/>
                            <OptionButton icon={<i className="fa-solid fa-share-nodes"></i>}
                                          text="Compartir" onClick={handleCopyLink} hidden={offlineMode}/>
                            <OptionButton icon={<i className="fa-solid fa-download"></i>}
                                          text="Descargar archivo" onClick={handleDownload}/>
                            {isOwner && !offlineMode && (
                                <>
                                    <SeparatorLine />
                                    <OptionButton
                                        icon={<i className="fa-solid fa-pen-to-square"></i>}
                                        text="Editar canción"
                                        onClick={() => {
                                            scrollRef.current?.close();
                                            setShowEditModal(true);
                                        }}
                                    />
                                    <OptionButton
                                        danger
                                        icon={<i className="fa-solid fa-trash"></i>}
                                        text="Eliminar canción"
                                        onClick={() => {
                                            scrollRef.current?.close();
                                            setShowDeleteConfirm(true);
                                        }}
                                    />
                                </>
                            )}
                            {extraOptions}
                        </>}

                    {selectingPlaylist &&
                        <>
                            <OptionButton icon={<i className="fa-solid fa-arrow-left"></i>}
                                          text="Volver" onClick={() => setSelectingPlaylist(false)}/>
                            {!isFavorite &&
                                <OptionButton hidden={!user?.uid} icon={<i className="fa-regular fa-heart"></i>}
                                              text="Añadir a favoritos" onClick={handleAddToFavorite}/>}
                            {isFavorite &&
                                <OptionButton hidden={!user?.uid} icon={<i className="fa-solid fa-heart-crack"></i>}
                                              text="Quitar de favoritos" onClick={handleRemoveFavorite}/>}
                            <OptionButton onClick={() => navigate("#cpsp2")} text="Crear playlist"
                                          icon={<i className="fa-solid fa-plus"></i>}/>
                            <SeparatorLine/>
                            {
                                playlists.map(playlist => (
                                    <OptionButton key={playlist.id} icon={loadingPlaylists ?
                                        <i className="fa-solid fa-spinner fa-spin"></i> :
                                        <i className="fa-solid fa-plus"></i>}
                                                  text={playlist.username} onClick={() => {
                                        if (loadingPlaylists) return;
                                        setLoadingPlaylists(true);
                                        Spotip17anFunctions.use('addToPlaylist').call({
                                            playlistId: playlist.id + "",
                                            songId: song!.id
                                        }).then(() => {
                                            toast.success('Canción añadida a la playlist');
                                            setSelectingPlaylist(false);
                                        }).catch(e => {
                                            toast.error(e.message);
                                        }).finally(() => {
                                            setLoadingPlaylists(false);
                                        });
                                    }}/>
                                ))
                            }
                        </>}
                </ScrollModal>
                <CreatePlaylistModal hash={'cpsp2'} onClose={handleCloseCreatePlaylist} onSave={handleSavePlaylist}/>

                {/* Modal for editing song */}
                <SongEditModal
                    show={showEditModal}
                    song={song}
                    onClose={() => setShowEditModal(false)}
                    onSave={(updatedSong) => {
                        setSong(updatedSong);
                    }}
                    onDelete={(songId) => {
                        onDeleteSong?.(songId);
                    }}
                />

                {/* Direct confirmation modal from context menu */}
                <ConfirmationModal
                    show={showDeleteConfirm}
                    title="Eliminar canción"
                    message="¿Seguro que quieres eliminar esta canción? Esta acción es permanente y se eliminará de todos los álbumes, playlists y listas de favoritos de los usuarios."
                    confirmText="Eliminar permanentemente"
                    cancelText="Cancelar"
                    confirmVariant="danger"
                    loading={deletingSong}
                    onClose={() => setShowDeleteConfirm(false)}
                    onConfirm={async () => {
                        if (!song) return;
                        setDeletingSong(true);
                        try {
                            await deleteSong(song.id);
                            removeSongFromStore(song.id);
                            toast.success("Canción eliminada correctamente");
                            setShowDeleteConfirm(false);
                            onDeleteSong?.(song.id);
                        } catch (error: any) {
                            console.error("Error al eliminar la canción:", error);
                            toast.error(error?.message || "Error al eliminar la canción");
                        } finally {
                            setDeletingSong(false);
                        }
                    }}
                />
            </div>
        );
    });

export default SongOptionsModalView;
